import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Tabs from 'app/components/Tabs';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import TeamAvatar from 'app/components/TeamAvatar';
import { cssVariable } from 'app/utils/style';
import { localizedHeroName } from 'app/i18n/utils';
import { groupForDisplay, round, roundForDisplay } from 'app/utils/number';
import DisplayContext from 'app/common/DisplayContext';
import ConfigContext from 'app/common/ConfigContext';
import StatValue from 'app/components/StatValue';
import { getCDNUrl } from 'app/utils/api_url';

import { getHighestDisplayedStats, getTabFromVariation, getVariationFromData } from './utils';
import style from './style.module.scss';

interface Props {
  data: SeriesStatsBlockMatch;
}

const SeriesStatsBlockGrad: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const variation = getVariationFromData(data);

  const [tab, setTab] = useState<'maps' | 'heroes' | 'teams' | 'players'>(
    getTabFromVariation(variation)
  );
  const { singleBlockDisplay } = useContext(DisplayContext);
  const { lang } = useContext(ConfigContext);

  const highestStats = getHighestDisplayedStats(data, tab);

  const hasData = Object.values(data).some(val =>
    Array.isArray(val) ? val.length !== 0 : val !== null
  );

  return (
    <BlockGradTemplate
      hasData={hasData}
      title={t('block-series_stats-title')}
      skipRenderIfNoData={!singleBlockDisplay}
    >
      <Tabs
        selectTab={setTab}
        selectedTab={tab}
        tabs={[variation === 'cs' ? 'maps' : 'heroes', 'teams', 'players']}
        translationKey="block-series_stats-tabs"
        compact
        className={style.tabs}
      />
      {tab === 'maps' && 'most_picked_maps' in data && (
        <ul className={style.heroesList}>
          {data.most_picked_maps.map(mapStat => (
            <li
              key={mapStat.map.id}
              className={style.map}
              style={cssVariable('hero', `url(${getCDNUrl(mapStat.map.image_url)})`)}
            >
              <div className={style.heroName}>{mapStat.map.name}</div>
              <div className={style.heroStats}>
                <div className={style.stat}>
                  <div className={style.statName}>{t('block-series_stats-map_stats-rounds')}</div>
                  <div className={style.statValue}>{mapStat.games_count}</div>
                </div>
                <div className={style.stat}>
                  <div className={style.statName}>
                    {t('block-series_stats-map_stats-t_winrate')}
                  </div>
                  <div className={classNames(style.statValue, style.statValueT)}>
                    <StatValue
                      value={mapStat.terrorists_winrate}
                      displayFunction={roundForDisplay(0, '%')}
                    />
                  </div>
                </div>
                <div className={style.stat}>
                  <div className={style.statName}>
                    {t('block-series_stats-map_stats-ct_winrate')}
                  </div>
                  <div className={classNames(style.statValue, style.statValueCT)}>
                    <StatValue
                      value={mapStat.counter_terrorists_winrate}
                      displayFunction={roundForDisplay(0, '%')}
                    />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tab === 'heroes' && 'most_picked_heroes' in data && (
        <ul className={style.heroesList}>
          {data.most_picked_heroes.map(hero => (
            <li
              key={hero.id}
              className={style.hero}
              style={cssVariable('hero', `url(${getCDNUrl(hero.image_url)})`)}
            >
              {/* TODO: handle LoL */}
              <div className={style.heroName}>{localizedHeroName(hero, 'dota2', t)}</div>
              <div className={style.heroStats}>
                <div className={style.stat}>
                  <div className={style.statName}>{t('block-series_stats-hero_stats-picks')}</div>
                  <div className={style.statValue}>{hero.number_of_picks}</div>
                </div>
                <div className={style.stat}>
                  <div className={style.statName}>{t('block-series_stats-hero_stats-bans')}</div>
                  <div className={style.statValue}>{hero.number_of_bans}</div>
                </div>
                <div className={style.stat}>
                  <div className={style.statName}>{t('block-series_stats-hero_stats-winrate')}</div>
                  <div className={style.statValue}>
                    {roundForDisplay(0, '%')(hero.winrate * 100)}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {(tab === 'teams' || tab === 'players') && (
        <ul className={style.highestStatsList}>
          {highestStats.map(stat => (
            <li key={stat.key} className={style.highestStat}>
              <div className={style.highestStatTeam}>
                <TeamAvatar team={stat.team} className={style.highestStatTeamLogo} />
                <div className={style.highestStatTeamNameContainer}>
                  <div className={style.highestStatNumberOfGames}>
                    {t('block-series_stats-number_of_games', { count: stat.games_count })}
                  </div>
                  <div className={style.highestStatTeamName}>{stat.name}</div>
                </div>
              </div>
              <div className={style.stat}>
                <div className={style.statName}>{t('block-series_stats-category')}</div>
                <div className={style.statValue}>{t(`block-series_stats-${stat.key}`)}</div>
              </div>
              <div className={style.statValue}>{groupForDisplay(round(stat.value, 2), lang)}</div>
            </li>
          ))}
        </ul>
      )}
    </BlockGradTemplate>
  );
};

SeriesStatsBlockGrad.displayName = 'SeriesStatsBlockGrad';

export default SeriesStatsBlockGrad;
